import { Component, EventEmitter, inject, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize, takeUntil } from 'rxjs';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { LoaderService } from '../../../../shared/services/loader/loader.service';
import { AuthService } from '../../services/auth.service';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';

@Component({
  selector: 'app-sme-signup',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './sme-signup.component.html',
  styleUrl: './sme-signup.component.scss',
})
export class SmeSignupComponent extends DestroyBase {
  authService = inject(AuthService);
  loaderService = inject(LoaderService);
  commonService = inject(CommonService);

  @Output() backToLogin = new EventEmitter<void>();

  signupEmail = '';
  isSigningUp = false;
  isSubmitted = false;

  onSignup(): void {
    if (!this.isValidEmail(this.signupEmail)) {
      this._showToast('Please enter a valid email address', 'error');
      return;
    }

    this.isSigningUp = true;
    this.loaderService.show();

    this.authService
      .smeSignup(this.signupEmail)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSigningUp = false;
          this.loaderService.hide();
        }),
      )
      .subscribe({
        next: () => {
          this.isSubmitted = true;
          // Return to login after 20 seconds automatically, or user clicks back
          setTimeout(() => {
            if (this.isSubmitted) {
              this.onBackToLogin();
            }
          }, 20000);
        },
        error: (error) => {
          const errorMessage =
            error.error?.message || 'Signup failed. Please try again.';
          this._showToast(errorMessage, 'error');
        },
      });
  }

  onBackToLogin(): void {
    this.backToLogin.emit();
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  private _showToast(message: string, type: 'success' | 'error'): void {
    this.commonService.triggerToast({
      type: type === 'success' ? ToastType.Success : ToastType.Error,
      message,
    });
  }
}
